import { Injectable } from '@angular/core';

import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, exhaustMap, filter, map, mergeMap, take } from 'rxjs/operators';

import { TranslocoService } from '@ngneat/transloco';

import {
  EnvironmentService,
  EventService,
  ExportActions,
  GridActions,
  GridService,
  ListService,
  NotificationService,
  fromList,
} from '@core/services';
import { errorDetail, errorDetailBlob } from '@util/http';
import { currentTimeInSeconds } from '@util/list';

import { SummaryRow, SummaryRowText } from '#types/grid';
import { MultipleListOfValuesResponse } from '#types/list';
import { OrderInquiryDetailResponseRow, OrderInquiryResponseRow } from '#types/order';

import { AuthService } from '@auth/services';
import { AuthActions } from '@auth/state';
import { CONSTANTS } from '@order/constants';
import { OrderService } from '@order/services';
import * as OrderActions from '@order/state/order.actions';

@Injectable()
export class OrderEffects {
  translate = (key: string | string[], params?: Record<string, string>, lang?: string) =>
    this.translocoService.translate(key, params, lang);

  constructor(
    private actions$: Actions,
    private store: Store,
    private orderService: OrderService,
    private gridService: GridService,
    private environmentService: EnvironmentService,
    private listService: ListService,
    private translocoService: TranslocoService,
    private notificationService: NotificationService,
    private authService: AuthService,
    private eventService: EventService
  ) {}

  RequestLists$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(OrderActions.RequestLists),
        concatLatestFrom(() => [this.store.select(fromList.selectOrderLists)]),
        map(([_, lists]) => {
          let lastUpdated = -1;

          for (const list of Object.keys(lists)) {
            // skip parameterized lists
            if (lists[list] && !Array.isArray(lists[list].data)) {
              continue;
            }

            if (lists[list]) {
              lastUpdated = Math.max(lastUpdated, currentTimeInSeconds() - (lists[list].updated ?? 0));
            }
          }

          if (lastUpdated === -1 || lastUpdated > this.environmentService.settings.secondsToCacheLists) {
            this.store.dispatch(OrderActions.RequestOrderLists());
          }
        })
      );
    },
    { dispatch: false }
  );

  RequestListsOnChangeScreen$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(AuthActions.ChangeScreen),
        filter((data) => data?.screen?.group === CONSTANTS.SCREEN_GROUP),
        concatLatestFrom(() => [this.store.select(fromList.selectOrderLists)]),
        map(([_, lists]) => {
          let lastUpdated = -1;

          for (const list of Object.keys(lists)) {
            // skip parameterized lists
            if (lists[list] && !Array.isArray(lists[list].data)) {
              continue;
            }

            if (lists[list]) {
              lastUpdated = Math.max(lastUpdated, currentTimeInSeconds() - (lists[list].updated ?? 0));
            }
          }

          if (lastUpdated === -1 || lastUpdated > this.environmentService.settings.secondsToCacheLists) {
            this.store.dispatch(OrderActions.RequestOrderLists());
          }
        })
      );
    },
    { dispatch: false }
  );

  requestOrderLists$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrderActions.RequestOrderLists),
      exhaustMap(() => {
        return this.listService.getListOfValues<MultipleListOfValuesResponse>('order').pipe(
          this.eventService.indicate(CONSTANTS.LIST_ALL),
          map((lists) => OrderActions.RequestOrderListsSuccess({ lists })),
          catchError((error) => of(OrderActions.RequestOrderListsFailure(error)))
        );
      })
    );
  });

  Initialize$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrderActions.InquiryInitialize, OrderActions.CreateOrderInitialize, OrderActions.InquiryDetailInitialize),
      map((screen) => AuthActions.ChangeScreen(screen))
    );
  });

  InquiryRequestData$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrderActions.InquiryRequestData, OrderActions.InquiryRequestDataWithParameters),
      concatLatestFrom(({ id }) => [
        this.gridService.filters$(id),
        this.gridService.page$(id),
        this.gridService.rows$(id),
        this.gridService.sortsObject$(id),
      ]),
      exhaustMap(([{ id }, filters, page, rows, sorts]) =>
        this.orderService.orderInquiry({ filters, page, rows, sorts }).pipe(
          map((data) => OrderActions.InquiryRequestDataSuccess({ id, data })),
          catchError((_error) => of(OrderActions.InquiryRequestDataFailure({ id, data: null })))
        )
      )
    );
  });

  InquiryRequestDataForExport$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrderActions.InquiryRequestDataForExport),
      concatLatestFrom(({ id }) => [this.gridService.filters$(id), this.gridService.sortsObject$(id)]),
      exhaustMap(([{ id, format, rows }, filters, sorts]) =>
        this.orderService.orderInquiry({ filters, page: 1, rows, sorts }).pipe(
          map((data) => ExportActions.RequestDataForExportSuccess({ id, format, data })),
          catchError((error) => of(ExportActions.RequestDataForExportFailure({ id, error })))
        )
      )
    );
  });

  InquiryCalculateSummaryRow$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(OrderActions.InquiryRequestDataSuccess, OrderActions.InquiryRequestDataFailure),
        concatLatestFrom(({ id }) => [this.gridService.data$<OrderInquiryResponseRow>(id)]),
        map(([{ id }, data]) => {
          if (!data || data.length === 0) {
            this.store.dispatch(GridActions.SetSummaryRow({ id, summaryRow: null }));
            return;
          }

          const summaryRow: SummaryRow = {
            ordershipid: 0,
            qtyship: 0,
            qtycommit: 0,
            qtypick: 0,
            qtyorder: 0,
            qtyrcvd: 0,
            weightentered: 0,
            weightorder: 0,
            weightcommit: 0,
            weightpick: 0,
            weightrcvd: 0,
            weight_entered_lbs: 0,
            weight_entered_kgs: 0,
          };

          for (const row of data) {
            summaryRow.ordershipid += 1;
            summaryRow.qtyship += row.qtyship ?? 0;
            summaryRow.qtycommit += row.qtycommit ?? 0;
            summaryRow.qtypick += row.qtypick ?? 0;
            summaryRow.qtyorder += row.qtyorder ?? 0;
            summaryRow.qtyrcvd += row.qtyrcvd ?? 0;
            summaryRow.weightentered += row.weightentered ?? 0;
            summaryRow.weightorder += row.weightorder ?? 0;
            summaryRow.weightcommit += row.weightcommit ?? 0;
            summaryRow.weightpick += row.weightpick ?? 0;
            summaryRow.weightrcvd += row.weightrcvd ?? 0;
            summaryRow.weight_entered_lbs += row.weight_entered_lbs ?? 0;
            summaryRow.weight_entered_kgs += row.weight_entered_kgs ?? 0;
          }

          this.store.dispatch(GridActions.SetSummaryRow({ id, summaryRow }));
        })
      );
    },
    { dispatch: false }
  );

  OrderInquiryUpdateRequestData$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrderActions.OrderInquiryUpdateRequestData),
      exhaustMap(({ payload }) =>
        this.orderService.orderInquiryUpdate(payload).pipe(
          map((res) => OrderActions.OrderInquiryUpdateRequestDataSuccess({ payload: res })),
          catchError((error) => of(OrderActions.OrderInquiryUpdateRequestDataFailure({ error })))
        )
      )
    );
  });

  InquiryShippingDetailRequestData$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        OrderActions.InquiryShippingDetailRequestData,
        OrderActions.InquiryShippingDetailRequestDataWithFilters,
        OrderActions.InquiryShippingDetailRequestDataWithParameters
      ),
      concatLatestFrom(({ id }) => [
        this.gridService.filters$(id),
        this.gridService.page$(id),
        this.gridService.rows$(id),
        this.gridService.sortsObject$(id),
      ]),
      exhaustMap(([{ id }, filters, page, rows, sorts]) =>
        this.orderService.orderInquiryShippingDetail({ filters, page, rows, sorts }).pipe(
          map((data) => OrderActions.InquiryShippingDetailRequestDataSuccess({ id, data })),
          catchError((_error) => of(OrderActions.InquiryShippingDetailRequestDataFailure({ id, data: null })))
        )
      )
    );
  });

  InquiryAttachmentRequestData$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        OrderActions.InquiryAttachmentRequestData,
        OrderActions.InquiryAttachmentRequestDataWithFilters,
        OrderActions.InquiryAttachmentRequestDataWithParameters
      ),
      concatLatestFrom(({ id }) => [
        this.gridService.filters$(id),
        this.gridService.page$(id),
        this.gridService.rows$(id),
        this.gridService.sortsObject$(id),
      ]),
      exhaustMap(([{ id }, filters, page, rows, sorts]) =>
        this.orderService.orderInquiryAttachment({ filters, page, rows, sorts }).pipe(
          map((data) => OrderActions.InquiryAttachmentRequestDataSuccess({ id, data })),
          catchError((_error) => of(OrderActions.InquiryAttachmentRequestDataFailure({ id, data: null })))
        )
      )
    );
  });

  InquiryAttachmentRequestDownload$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrderActions.InquiryAttachmentRequestDownload),
      mergeMap(({ filePath }) =>
        this.orderService.orderInquiryDownloadAttachment({ filePath }).pipe(
          map((data) => OrderActions.InquiryAttachmentRequestDownloadSuccess({ filePath, data })),
          catchError((error) => of(OrderActions.InquiryAttachmentRequestDownloadFailure({ error })))
        )
      )
    );
  });

  InquiryAttachmentRequestDownloadSuccess$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(OrderActions.InquiryAttachmentRequestDownloadSuccess),
        map(({ filePath, data }) => {
          this.orderService.download(filePath.replace(/\\+/g, '/').split('/').pop(), 'application/octet-stream', data);
        })
      );
    },
    { dispatch: false }
  );

  InquiryAttachmentRequestDownloadFailure$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(OrderActions.InquiryAttachmentRequestDownloadFailure),
        map(({ error }) => {
          errorDetailBlob(error)
            .then((detail) => {
              this.notificationService.error({
                message: detail?.message,
              });
            })
            .catch((err) => {
              console.error(err);
            });
        })
      );
    },
    { dispatch: false }
  );

  OrderInquiryCancelOrderRequest$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrderActions.OrderInquiryCancelOrderRequest),
      exhaustMap(({ orderid, shipid, fromDetailScreen }) =>
        this.orderService.orderInquiryCancelOrder({ orderid, shipid }).pipe(
          map((data) => OrderActions.OrderInquiryCancelOrderRequestSuccess({ orderid, shipid, fromDetailScreen, data })),
          catchError((error) => of(OrderActions.OrderInquiryCancelOrderRequestFailure({ error })))
        )
      )
    );
  });

  OrderInquiryCancelOrderRequestSuccess$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(OrderActions.OrderInquiryCancelOrderRequestSuccess),
        map(({ data, fromDetailScreen }) => {
          this.notificationService.success({
            title: this.translate('order.inquiry.cancel_order.success'),
            message: data[0].message ?? '',
          });

          if (fromDetailScreen) {
            this.orderService.detail$.pipe(take(1)).subscribe((detail) => {
              this.store.dispatch(
                OrderActions.InquiryDetailSetData({
                  data: { ...detail, modifiable: 'N', orderstatus: 'X', orderstatusabbrev: 'Cancelled' },
                })
              );

              this.gridService.gridRefresh$.next(CONSTANTS.GRID_ORDER_INQUIRY_DETAIL);
            });
          }
        })
      );
    },
    { dispatch: false }
  );

  OrderInquiryCancelOrderRequestFailure$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(OrderActions.OrderInquiryCancelOrderRequestFailure),
        map(({ error }) => {
          const detail = errorDetail(error);

          this.notificationService.error({
            title: this.translate('order.inquiry.cancel_order.error'),
            message: detail?.message,
          });
        })
      );
    },
    { dispatch: false }
  );

  OrderInquiryLockOrderRequest$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrderActions.OrderInquiryLockOrderRequest),
      exhaustMap(({ orderid, shipid, fromDetailScreen }) =>
        this.orderService.orderInquiryLockOrder({ orderid, shipid }).pipe(
          map((data) => OrderActions.OrderInquiryLockOrderRequestSuccess({ orderid, shipid, fromDetailScreen, data })),
          catchError((error) => of(OrderActions.OrderInquiryLockOrderRequestFailure({ error })))
        )
      )
    );
  });

  OrderInquiryLockOrderRequestSuccess$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(OrderActions.OrderInquiryLockOrderRequestSuccess),
        map(({ data, fromDetailScreen }) => {
          this.notificationService.success({
            title: this.translate('order.inquiry.lock_order.success'),
            message: data[0].message ?? '',
          });

          if (fromDetailScreen) {
            this.orderService.detail$.pipe(take(1)).subscribe((detail) => {
              this.store.dispatch(
                OrderActions.InquiryDetailSetData({ data: { ...detail, modifiable: 'N', orderstatus: '1', orderstatusabbrev: 'Entered' } })
              );

              this.gridService.gridRefresh$.next(CONSTANTS.GRID_ORDER_INQUIRY_DETAIL);
            });
          }
        })
      );
    },
    { dispatch: false }
  );

  OrderInquiryLockOrderRequestFailure$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(OrderActions.OrderInquiryLockOrderRequestFailure),
        map(({ error }) => {
          const detail = errorDetail(error);

          this.notificationService.error({
            title: this.translate('order.inquiry.lock_order.error'),
            message: detail?.message,
          });
        })
      );
    },
    { dispatch: false }
  );

  InquiryDetailRequestData$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        OrderActions.InquiryDetailRequestData,
        OrderActions.InquiryDetailRequestDataWithParameters,
        OrderActions.InquiryDetailRequestDataWithFilters
      ),
      concatLatestFrom(({ id }) => [
        this.gridService.filters$(id),
        this.gridService.page$(id),
        this.gridService.rows$(id),
        this.gridService.sortsObject$(id),
      ]),
      exhaustMap(([{ id }, filters, page, rows, sorts]) =>
        this.orderService.orderDetail({ filters, page, rows, sorts }).pipe(
          map((data) => OrderActions.InquiryDetailRequestDataSuccess({ id, data })),
          catchError((_error) => of(OrderActions.InquiryDetailRequestDataFailure({ id, data: null })))
        )
      )
    );
  });

  InquiryDetailRequestDataForExport$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrderActions.InquiryDetailRequestDataForExport),
      concatLatestFrom(({ id }) => [this.gridService.filters$(id), this.gridService.sortsObject$(id)]),
      exhaustMap(([{ id, format, rows }, filters, sorts]) =>
        this.orderService.orderDetail({ filters, page: 1, rows, sorts }).pipe(
          map((data) => ExportActions.RequestDataForExportSuccess({ id, format, data })),
          catchError((error) => of(ExportActions.RequestDataForExportFailure({ id, error })))
        )
      )
    );
  });

  InquiryDetailCalculateSummaryRow$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(OrderActions.InquiryDetailRequestDataSuccess, OrderActions.InquiryDetailRequestDataFailure),
        concatLatestFrom(({ id }) => [this.gridService.data$<OrderInquiryDetailResponseRow>(id)]),
        map(([{ id }, data]) => {
          if (!data || data.length === 0) {
            this.store.dispatch(GridActions.SetSummaryRow({ id, summaryRow: null }));
            return;
          }

          const summaryRowText: SummaryRowText = {
            item: this.translate('inventory.item_detail.grid.summary.total'),
          };

          this.store.dispatch(GridActions.SetSummaryRowText({ id, summaryRowText }));

          const summaryRow: SummaryRow = {
            weightorder: 0,
            weightcommit: 0,
            weightship: 0,
            weightpick: 0,
            weightrcvd: 0,
            qtyorder: 0,
            qtyrcvd: 0,
            qtycommit: 0,
            qtyship: 0,
            shipvariance: 0,
            rcvdvariance: 0,
            qtyentered: 0,
            qtypick: 0,
            amt2check: 0,
            amt2pack: 0,
            amt2sort: 0,
            amtcommit: 0,
            amtorder: 0,
            amtpick: 0,
            amtrcvd: 0,
            amtrcvddmgd: 0,
            amtrcvdgood: 0,
            amtship: 0,
            amttotcommit: 0,
            cube2check: 0,
            cube2pack: 0,
            cube2sort: 0,
            cubecommit: 0,
            cubeorder: 0,
            cubepick: 0,
            cubercvd: 0,
            cubercvddmgd: 0,
            cubercvdgood: 0,
            cubeship: 0,
            cubetotcommit: 0,
            qty2check: 0,
            qty2pack: 0,
            qty2sort: 0,
            qtyorderdiff: 0,
            qtyoverpick: 0,
            qtyrcvddmgd: 0,
            qtyrcvdgood: 0,
            qtyreturnedtostock: 0,
            qtyscrapped: 0,
            qtytotcommit: 0,
            weight2check: 0,
            weight2pack: 0,
            weight2sort: 0,
            weightrcvddmgd: 0,
            weightrcvdgood: 0,
            weighttotcommit: 0,
            weight_entered_kgs: 0,
            weight_entered_lbs: 0,
          };

          for (const row of data) {
            summaryRow.weightorder += row.weightorder ?? 0;
            summaryRow.weightcommit += row.weightcommit ?? 0;
            summaryRow.weightship += row.weightship ?? 0;
            summaryRow.weightpick += row.weightpick ?? 0;
            summaryRow.weightrcvd += row.weightrcvd ?? 0;
            summaryRow.qtyorder += row.qtyorder ?? 0;
            summaryRow.qtyrcvd += row.qtyrcvd ?? 0;
            summaryRow.qtycommit += row.qtycommit ?? 0;
            summaryRow.qtyship += row.qtyship ?? 0;
            summaryRow.shipvariance += row.shipvariance ?? 0;
            summaryRow.rcvdvariance += row.rcvdvariance ?? 0;
            summaryRow.qtyentered += row.qtyentered ?? 0;
            summaryRow.qtypick += row.qtypick ?? 0;
            summaryRow.amt2check += row.amt2check ?? 0;
            summaryRow.amt2pack += row.amt2pack ?? 0;
            summaryRow.amt2sort += row.amt2sort ?? 0;
            summaryRow.amtcommit += row.amtcommit ?? 0;
            summaryRow.amtorder += row.amtorder ?? 0;
            summaryRow.amtpick += row.amtpick ?? 0;
            summaryRow.amtrcvd += row.amtrcvd ?? 0;
            summaryRow.amtrcvddmgd += row.amtrcvddmgd ?? 0;
            summaryRow.amtrcvdgood += row.amtrcvdgood ?? 0;
            summaryRow.amtship += row.amtship ?? 0;
            summaryRow.amttotcommit += row.amttotcommit ?? 0;
            summaryRow.cube2check += row.cube2check ?? 0;
            summaryRow.cube2pack += row.cube2pack ?? 0;
            summaryRow.cube2sort += row.cube2sort ?? 0;
            summaryRow.cubecommit += row.cubecommit ?? 0;
            summaryRow.cubeorder += row.cubeorder ?? 0;
            summaryRow.cubepick += row.cubepick ?? 0;
            summaryRow.cubercvd += row.cubercvd ?? 0;
            summaryRow.cubercvddmgd += row.cubercvddmgd ?? 0;
            summaryRow.cubercvdgood += row.cubercvdgood ?? 0;
            summaryRow.cubeship += row.cubeship ?? 0;
            summaryRow.cubetotcommit += row.cubetotcommit ?? 0;
            summaryRow.qty2check += row.qty2check ?? 0;
            summaryRow.qty2pack += row.qty2pack ?? 0;
            summaryRow.qty2sort += row.qty2sort ?? 0;
            summaryRow.qtyorderdiff += row.qtyorderdiff ?? 0;
            summaryRow.qtyoverpick += row.qtyoverpick ?? 0;
            summaryRow.qtyrcvddmgd += row.qtyrcvddmgd ?? 0;
            summaryRow.qtyrcvdgood += row.qtyrcvdgood ?? 0;
            summaryRow.qtyreturnedtostock += row.qtyreturnedtostock ?? 0;
            summaryRow.qtyscrapped += row.qtyscrapped ?? 0;
            summaryRow.qtytotcommit += row.qtytotcommit ?? 0;
            summaryRow.weight2check += row.weight2check ?? 0;
            summaryRow.weight2pack += row.weight2pack ?? 0;
            summaryRow.weight2sort += row.weight2sort ?? 0;
            summaryRow.weightrcvddmgd += row.weightrcvddmgd ?? 0;
            summaryRow.weightrcvdgood += row.weightrcvdgood ?? 0;
            summaryRow.weighttotcommit += row.weighttotcommit ?? 0;
            summaryRow.weight_entered_kgs += row.weight_entered_kgs ?? 0;
            summaryRow.weight_entered_lbs += row.weight_entered_lbs ?? 0;
          }

          this.store.dispatch(GridActions.SetSummaryRow({ id, summaryRow }));
        })
      );
    },
    { dispatch: false }
  );

  AddItemDialogStep1ToComplete$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrderActions.AddItemDialogStep1ToComplete),
      concatLatestFrom(() => [this.orderService.addItem$, this.orderService.detail$]),
      exhaustMap(([_, data, detail]) =>
        this.orderService
          .orderAddItem({
            orderid: detail.orderid,
            shipid: detail.shipid,
            fromfacility: detail.fromfacility,
            item: data.item,
            lotNumber: data.lotNumber,
            uom: data.uom,
            weightOrUom: data.weightOrUom,
            quantity: data.quantity,
            weight: data.weight,
            weightUom: data.weightUom,
          })
          .pipe(
            map((response) => OrderActions.AddItemDialogSuccess({ response })),
            catchError((error) => of(OrderActions.AddItemDialogFailure({ error })))
          )
      )
    );
  });

  AddItemDialogStep2ToComplete$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrderActions.AddItemDialogStep2ToComplete),
      concatLatestFrom(() => [this.orderService.addItem$, this.orderService.detail$]),
      exhaustMap(([passthru, data, detail]) =>
        this.orderService
          .orderAddItem({
            orderid: detail.orderid,
            shipid: detail.shipid,
            fromfacility: detail.fromfacility,
            item: data.item,
            lotNumber: data.lotNumber,
            uom: data.uom,
            weightOrUom: data.weightOrUom,
            quantity: data.quantity,
            weight: data.weight,
            weightUom: data.weightUom,
            dtlpassthruchar01: passthru.dtlpassthruchar01,
            dtlpassthruchar02: passthru.dtlpassthruchar02,
            dtlpassthruchar03: passthru.dtlpassthruchar03,
            dtlpassthruchar04: passthru.dtlpassthruchar04,
            dtlpassthruchar05: passthru.dtlpassthruchar05,
            dtlpassthruchar06: passthru.dtlpassthruchar06,
            dtlpassthruchar07: passthru.dtlpassthruchar07,
            dtlpassthruchar08: passthru.dtlpassthruchar08,
            dtlpassthruchar09: passthru.dtlpassthruchar09,
            dtlpassthruchar10: passthru.dtlpassthruchar10,
            dtlpassthruchar11: passthru.dtlpassthruchar11,
            dtlpassthruchar12: passthru.dtlpassthruchar12,
            dtlpassthruchar13: passthru.dtlpassthruchar13,
            dtlpassthruchar14: passthru.dtlpassthruchar14,
            dtlpassthruchar15: passthru.dtlpassthruchar15,
            dtlpassthruchar16: passthru.dtlpassthruchar16,
            dtlpassthruchar17: passthru.dtlpassthruchar17,
            dtlpassthruchar18: passthru.dtlpassthruchar18,
            dtlpassthruchar19: passthru.dtlpassthruchar19,
            dtlpassthruchar20: passthru.dtlpassthruchar20,
            dtlpassthruchar21: passthru.dtlpassthruchar21,
            dtlpassthruchar22: passthru.dtlpassthruchar22,
            dtlpassthruchar23: passthru.dtlpassthruchar23,
            dtlpassthruchar24: passthru.dtlpassthruchar24,
            dtlpassthruchar25: passthru.dtlpassthruchar25,
            dtlpassthruchar26: passthru.dtlpassthruchar26,
            dtlpassthruchar27: passthru.dtlpassthruchar27,
            dtlpassthruchar28: passthru.dtlpassthruchar28,
            dtlpassthruchar29: passthru.dtlpassthruchar29,
            dtlpassthruchar30: passthru.dtlpassthruchar30,
            dtlpassthruchar31: passthru.dtlpassthruchar31,
            dtlpassthruchar32: passthru.dtlpassthruchar32,
            dtlpassthruchar33: passthru.dtlpassthruchar33,
            dtlpassthruchar34: passthru.dtlpassthruchar34,
            dtlpassthruchar35: passthru.dtlpassthruchar35,
            dtlpassthruchar36: passthru.dtlpassthruchar36,
            dtlpassthruchar37: passthru.dtlpassthruchar37,
            dtlpassthruchar38: passthru.dtlpassthruchar38,
            dtlpassthruchar39: passthru.dtlpassthruchar39,
            dtlpassthruchar40: passthru.dtlpassthruchar40,
            dtlpassthrudate01: passthru.dtlpassthrudate01,
            dtlpassthrudate02: passthru.dtlpassthrudate02,
            dtlpassthrudate03: passthru.dtlpassthrudate03,
            dtlpassthrudate04: passthru.dtlpassthrudate04,
            dtlpassthrudoll01: passthru.dtlpassthrudoll01,
            dtlpassthrudoll02: passthru.dtlpassthrudoll02,
            dtlpassthrunum01: passthru.dtlpassthrunum01,
            dtlpassthrunum02: passthru.dtlpassthrunum02,
            dtlpassthrunum03: passthru.dtlpassthrunum03,
            dtlpassthrunum04: passthru.dtlpassthrunum04,
            dtlpassthrunum05: passthru.dtlpassthrunum05,
            dtlpassthrunum06: passthru.dtlpassthrunum06,
            dtlpassthrunum07: passthru.dtlpassthrunum07,
            dtlpassthrunum08: passthru.dtlpassthrunum08,
            dtlpassthrunum09: passthru.dtlpassthrunum09,
            dtlpassthrunum10: passthru.dtlpassthrunum10,
            dtlpassthrunum11: passthru.dtlpassthrunum11,
            dtlpassthrunum12: passthru.dtlpassthrunum12,
            dtlpassthrunum13: passthru.dtlpassthrunum13,
            dtlpassthrunum14: passthru.dtlpassthrunum14,
            dtlpassthrunum15: passthru.dtlpassthrunum15,
            dtlpassthrunum16: passthru.dtlpassthrunum16,
            dtlpassthrunum17: passthru.dtlpassthrunum17,
            dtlpassthrunum18: passthru.dtlpassthrunum18,
            dtlpassthrunum19: passthru.dtlpassthrunum19,
            dtlpassthrunum20: passthru.dtlpassthrunum20,
          })
          .pipe(
            map((response) => OrderActions.AddItemDialogSuccess({ response })),
            catchError((error) => of(OrderActions.AddItemDialogFailure({ error })))
          )
      )
    );
  });

  AddItemDialogSuccess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrderActions.AddItemDialogSuccess),
      concatLatestFrom(() => [this.orderService.addItem$, this.orderService.detail$]),
      map(([{ response }, data, detail]) => {
        let message: string;

        if (response[0].status === 'OKAY') {
          if (!!data.lotNumber && data.lotNumber !== '') {
            message = this.translate('order.add_item_dialog.step_2.success.item_added_with_lot', {
              item: data.item,
              lotNumber: data.lotNumber,
              orderIdShipId: detail.ordershipid,
            });
          } else {
            message = this.translate('order.add_item_dialog.step_2.success.item_added', {
              item: data.item,
              orderIdShipId: detail.ordershipid,
            });
          }
        } else {
          message = response[0].message;
        }

        this.notificationService.success({ id: 'order-item-added', message });
        this.gridService.gridRefresh$.next(CONSTANTS.GRID_ORDER_INQUIRY_DETAIL);

        return OrderActions.AddItemDialogClose();
      })
    );
  });

  AddItemDialogFailure$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrderActions.AddItemDialogFailure),
      concatLatestFrom(() => [this.authService.hasPassThru$]),
      map(([{ error }, hasPassThru]) => {
        const detail = errorDetail(error);

        let message = detail.message;
        let nextStep = 2;

        if (message === 'orderdtl-constraint-violation') {
          message = this.translate('order.add_item_dialog.step_2.errors.orderdtl_constraint_violation');
          nextStep = 1;
        }

        if (hasPassThru === 'N' && nextStep === 2) {
          nextStep = 1;
        }

        this.notificationService.error({ message });

        return OrderActions.AddItemDialogSetStep({ step: nextStep });
      })
    );
  });

  //
  // order / create
  //

  CreateOrderInit$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrderActions.CreateOrderInit),
      map(({ payload, uniqueOrderIdentifier }) => OrderActions.OrderDuplicateCheckRequest({ payload, uniqueOrderIdentifier }))
    );
  });

  OrderCreateRequest$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrderActions.CreateOrderRequest),
      exhaustMap(({ payload }) =>
        this.orderService.orderCreate(payload).pipe(
          map((data) => OrderActions.CreateOrderRequestSuccess({ payload, data })),
          catchError((error) => of(OrderActions.CreateOrderRequestFailure({ payload, error })))
        )
      )
    );
  });

  OrderCreateRequestSuccess$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(OrderActions.CreateOrderRequestSuccess),
        map(({ data }) => {
          let message = data[0].message;

          if (message === 'order-created') {
            if (data[0].ordertype === 'R') {
              message = this.translate('order.create_order.success.inbound_order_created', {
                orderid: `${data[0].orderid}`,
                shipid: `${data[0].shipid}`,
              });
            } else {
              message = this.translate('order.create_order.success.outbound_order_created', {
                orderid: `${data[0].orderid}`,
                shipid: `${data[0].shipid}`,
              });
            }
          }

          this.notificationService.success({
            id: 'order-created',
            message,
            payload: data[0],
          });
        })
      );
    },
    { dispatch: false }
  );

  OrderCreateRequestFailure$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(OrderActions.CreateOrderRequestFailure),
        map(({ error }) => {
          const detail = errorDetail(error);

          this.notificationService.error({
            message: detail?.message,
          });
        })
      );
    },
    { dispatch: false }
  );

  //
  // order / duplicate reference and po check
  //

  OrderDuplicateCheckRequest$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrderActions.OrderDuplicateCheckRequest),
      exhaustMap(({ payload, uniqueOrderIdentifier }) =>
        this.orderService
          .orderDuplicateCheck({
            orderType: payload.orderType,
            reference: payload[payload.orderType].reference,
            po: payload[payload.orderType].po,
            uniqueOrderIdentifier,
          })
          .pipe(
            map(() => OrderActions.OrderDuplicateCheckRequestSuccess({ payload })),
            catchError((error) => of(OrderActions.OrderDuplicateCheckRequestFailure({ payload, error })))
          )
      )
    );
  });

  OrderDuplicateCheckRequestSuccess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrderActions.OrderDuplicateCheckRequestSuccess),
      map(({ payload }) => OrderActions.CreateOrderRequest({ payload }))
    );
  });

  OrderDuplicateCheckRequestFailure$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(OrderActions.OrderDuplicateCheckRequestFailure),
        map(({ payload, error }) => {
          const detail = errorDetail(error);

          if (detail.statusCode === 422 && detail?.data) {
            const [uniqueOrderIdentifier, duplicateReferenceYesNoWarning, duplicateCount] = detail.data.split(':');

            this.orderService.duplicateOrderConfirmation(payload, uniqueOrderIdentifier, duplicateReferenceYesNoWarning, duplicateCount);
          } else {
            this.notificationService.error({
              message: detail?.message,
            });
          }
        })
      );
    },
    { dispatch: false }
  );

  //
  // order / update / duplicate reference and po check
  //

  OrderUpdateDuplicateCheckRequest$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrderActions.OrderUpdateDuplicateCheckRequest),
      exhaustMap(({ payload, uniqueOrderIdentifier, index }) =>
        this.orderService
          .orderDuplicateCheck({
            orderType: payload.ordertype,
            reference: payload.reference,
            po: payload.po,
            uniqueOrderIdentifier,
            orderid: payload.orderid,
            shipid: payload.shipid,
          })
          .pipe(
            map(() => OrderActions.OrderUpdateDuplicateCheckRequestSuccess({ payload, index })),
            catchError((error) => of(OrderActions.OrderUpdateDuplicateCheckRequestFailure({ payload, index, error })))
          )
      )
    );
  });

  OrderUpdateDuplicateCheckRequestSuccess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrderActions.OrderUpdateDuplicateCheckRequestSuccess),
      map(({ payload, index }) => {
        this.gridService.rowEditSave$.next({ index, data: payload });
        return OrderActions.OrderInquiryUpdateRequestData({ payload });
      })
    );
  });

  OrderUpdateDuplicateCheckRequestFailure$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(OrderActions.OrderUpdateDuplicateCheckRequestFailure),
        map(({ payload, index, error }) => {
          const detail = errorDetail(error);

          if (detail.statusCode === 422 && detail?.data) {
            const [uniqueOrderIdentifier, duplicateReferenceYesNoWarning, duplicateCount] = detail.data.split(':');

            this.orderService.duplicateOrderUpdateConfirmation(
              payload,
              uniqueOrderIdentifier,
              duplicateReferenceYesNoWarning,
              duplicateCount,
              index
            );
          } else {
            this.notificationService.error({
              message: detail?.message,
            });
          }
        })
      );
    },
    { dispatch: false }
  );

  //
  // order / delete item
  //

  OrderDeleteItemRequest$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrderActions.OrderDeleteItemRequest),
      exhaustMap(({ orderid, shipid, item, lotNumber }) =>
        this.orderService.orderDeleteItem({ orderid, shipid, item, lotNumber }).pipe(
          map(() => OrderActions.OrderDeleteItemRequestSuccess({ orderid, shipid, item, lotNumber })),
          catchError((error) => of(OrderActions.OrderDeleteItemRequestFailure({ error })))
        )
      )
    );
  });

  OrderDeleteItemRequestSuccess$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(OrderActions.OrderDeleteItemRequestSuccess),
        map(({ orderid, shipid, item, lotNumber }) => {
          const title = this.translate('order.detail.delete_item.success.title');
          let message: string;

          if (lotNumber) {
            message = this.translate('order.detail.delete_item.success.message_item_lot', {
              orderId: `${orderid}`,
              shipId: `${shipid}`,
              item,
              lotNumber,
            });
          } else {
            message = this.translate('order.detail.delete_item.success.message_item', {
              orderId: `${orderid}`,
              shipId: `${shipid}`,
              item,
            });
          }

          this.notificationService.success({
            id: 'order-delete-item',
            title,
            message,
          });
        })
      );
    },
    { dispatch: false }
  );

  OrderDeleteItemRequestFailure$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(OrderActions.OrderDeleteItemRequestFailure),
        map(({ error }) => {
          const detail = errorDetail(error);

          this.notificationService.error({
            message: detail?.message,
          });
        })
      );
    },
    { dispatch: false }
  );

  //
  // detail / update item
  //

  UpdateItemRequest$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrderActions.UpdateItemRequest),
      exhaustMap(({ payload }) =>
        this.orderService.orderUpdateItem(payload).pipe(
          map((data) => OrderActions.UpdateItemRequestSuccess({ payload, data })),
          catchError((error) => of(OrderActions.UpdateItemRequestFailure({ error })))
        )
      )
    );
  });

  UpdateItemRequestSuccess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OrderActions.UpdateItemRequestSuccess),
      map(({ payload, data }) => {
        let message: string;

        if (data[0].status === 'OKAY') {
          if (!!payload.lotNumber && payload.lotNumber !== '') {
            message = this.translate('order.detail.update.success.item_updated_with_lot', {
              item: payload.item,
              lotNumber: payload.lotNumber,
            });
          } else {
            message = this.translate('order.detail.update.success.item_updated', {
              item: payload.item,
            });
          }
        } else {
          message = data[0].message;
        }

        this.notificationService.success({ id: 'order-item-updated', message });

        this.gridService.gridRefresh$.next(CONSTANTS.GRID_ORDER_INQUIRY_DETAIL);

        return OrderActions.UpdateItemDialogClose();
      })
    );
  });

  UpdateItemRequestFailure$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(OrderActions.UpdateItemRequestFailure),
        map(({ error }) => {
          const detail = errorDetail(error);

          this.notificationService.error({
            message: detail?.message,
          });
        })
      );
    },
    { dispatch: false }
  );
}
